import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { buildStoreProductPath, fetchStoreData } from '../lib/store';

function StoreProductCard({ product }) {
  const productPath = buildStoreProductPath(product);

  return (
    <Link to={productPath} className="block">
      <article className="overflow-hidden rounded-[20px] border border-slate-200 bg-white/88 transition-colors hover:border-slate-300">
        <div className="aspect-[4/3] bg-slate-100">
          {product.image ? (
            <img
              src={product.image}
              alt={product.name}
              className="h-full w-full object-cover"
              loading="lazy"
            />
          ) : (
            <div className="flex h-full items-center justify-center text-[11px] uppercase tracking-[0.28em] text-slate-400">
              No image
            </div>
          )}
        </div>

        <div className="p-5">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <h2 className="text-lg font-medium leading-tight text-slate-900">{product.name}</h2>
              {product.shortDescription && (
                <p className="mt-2 text-sm leading-6 text-slate-600">{product.shortDescription}</p>
              )}
            </div>

            {product.price && <p className="text-sm text-slate-900">{product.price}</p>}
          </div>

          <p className="mt-4 text-[11px] uppercase tracking-[0.22em] text-slate-500">
            {product.inStock ? 'In stock' : 'Sold out'}
          </p>
        </div>
      </article>
    </Link>
  );
}

export default function StorePage() {
  const {
    data: storeData = { meta: {}, productCards: [] },
    isLoading: storeLoading,
    error: storeError,
  } = useQuery({
    queryKey: ['storeProducts'],
    queryFn: fetchStoreData,
  });

  const productCards = storeData.productCards;
  const subtitle =
    typeof storeData.meta?.description === 'string' ? storeData.meta.description.trim() : '';

  return (
    <main className="page-shell">
      <div className="mx-auto w-full max-w-5xl">
        <header className="border-b border-slate-200 pb-6">
          <p className="text-[11px] uppercase tracking-[0.28em] text-slate-500">Encreage</p>

          <div className="mt-4 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
            <div className="max-w-2xl">
              <h1 className="text-4xl font-medium tracking-tight text-slate-900 md:text-5xl">
                Store
              </h1>
              {subtitle && <p className="mt-3 text-sm leading-7 text-slate-600">{subtitle}</p>}
            </div>

            {!storeLoading && !storeError && productCards.length > 0 && (
              <p className="text-[11px] uppercase tracking-[0.22em] text-slate-500">
                {productCards.length} products
              </p>
            )}
          </div>
        </header>

        {storeLoading ? (
          <div className="py-16 text-sm text-slate-500">Loading products...</div>
        ) : storeError ? (
          <div className="py-16 text-sm text-slate-500">Catalog unavailable right now.</div>
        ) : productCards.length > 0 ? (
          <section className="mt-8 grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
            {productCards.map((product) => (
              <StoreProductCard key={product.slug || product.name} product={product} />
            ))}
          </section>
        ) : (
          <div className="py-16 text-sm text-slate-500">No products available right now.</div>
        )}
      </div>
    </main>
  );
}
